/**
 * Unsent text kept in sessionStorage, per tab, so a reload (a new build, a
 * crash) gives back what was being typed. Session, not local: a draft belongs
 * to the tab it was typed in and goes when the tab closes.
 */
import { useEffect, useLayoutEffect, useRef } from "react";

const PREFIX = "caseblink.draft:";

export const readSessionDraft = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
};

/** Keep `value` under `key`; an empty value forgets it. */
export const writeSessionDraft = (key: string, value: string | null) => {
  try {
    if (value) window.sessionStorage.setItem(PREFIX + key, value);
    else window.sessionStorage.removeItem(PREFIX + key);
  } catch {
    // Storage full or blocked: the draft just isn't kept.
  }
};

export const clearSessionDraft = (key: string) => writeSessionDraft(key, null);

/**
 * Keep `encoded` (the draft as a string, or null when there is nothing to
 * keep) under `key`, and hand back what was kept when `key` is first seen.
 * The restore runs before the first save for that key, so a draft is never
 * overwritten by the empty input it is about to fill.
 */
export const useSessionDraft = (
  key: string | null,
  encoded: string | null,
  restore: (saved: string) => void
) => {
  const restoredFor = useRef<string | null>(null);
  const restoreRef = useRef(restore);
  restoreRef.current = restore;

  useLayoutEffect(() => {
    if (!key || restoredFor.current === key) return;
    restoredFor.current = key;
    const saved = readSessionDraft(key);
    if (saved) restoreRef.current(saved);
  }, [key]);

  const skipFirst = useRef<string | null>(null);
  useEffect(() => {
    if (!key || restoredFor.current !== key) return;
    // The render that restored still holds the empty input; its save would
    // erase the draft the next render shows.
    if (skipFirst.current !== key) {
      skipFirst.current = key;
      if (!encoded) return;
    }
    writeSessionDraft(key, encoded);
  }, [key, encoded]);
};
